/**
 * Klaviyo "Added to Cart" Event Tracking
 *
 * Pushes rich cart data (including custom design preview URLs) to Klaviyo
 * via the _learnq JS API after successful cart additions.
 *
 * Three cart add paths covered:
 *   1. AJAX unified submit — listens to cart:updated event (has product_id in detail)
 *   2. PitchPrint POST + page reload — checks sessionStorage flags at init
 *   3. PitchPrint POST + no reload (Be Yours intercepts) — polls sessionStorage flags
 *
 * Data: Fetches /cart.js for full cart state, extracts preview image URLs
 */
(() => {
  'use strict';

  const LOG_PREFIX = '[KlaviyoCartTracking]';
  const EVENT_NAME = 'Added to Cart';
  const DEBOUNCE_MS = 3000;
  const KLAVIYO_TIMEOUT_MS = 5000;

  const log = (...args) => window.PITCHPRINT_DEBUG && console.log(LOG_PREFIX, ...args);

  const config = window.__klaviyoCartTracking;
  if (!config || !config.enabled) return;

  let lastEventTime = 0;

  // --- Klaviyo helpers ---

  function waitForKlaviyo() {
    return new Promise((resolve) => {
      if (window._learnq) { resolve(true); return; }

      const interval = setInterval(() => {
        if (window._learnq) {
          clearInterval(interval);
          resolve(true);
        }
      }, 200);

      setTimeout(() => {
        clearInterval(interval);
        resolve(!!window._learnq);
      }, KLAVIYO_TIMEOUT_MS);
    });
  }

  function identifyCustomer() {
    if (!config.customer.loggedIn || !config.customer.email) return;

    try {
      const payload = { '$email': config.customer.email };
      if (config.customer.firstName) payload['$first_name'] = config.customer.firstName;
      if (config.customer.lastName) payload['$last_name'] = config.customer.lastName;
      window._learnq.push(['identify', payload]);
      log('Customer identified:', config.customer.email);
    } catch (err) {
      log('Error identifying customer:', err);
    }
  }

  // --- Image URL helpers ---

  function getPreviewImageUrl(item) {
    const props = item.properties || {};

    if (props._pitchprint_preview_url) return props._pitchprint_preview_url;
    if (props._front_file_cropped_url) return props._front_file_cropped_url;
    if (props._file_upload_front_preview) return props._file_upload_front_preview;

    if (item.image) {
      return item.image.startsWith('//') ? 'https:' + item.image : item.image;
    }
    return '';
  }

  function getBackPreviewImageUrl(item) {
    const props = item.properties || {};

    if (props._pitchprint_preview_url_back) return props._pitchprint_preview_url_back;
    if (props._back_file_cropped_url) return props._back_file_cropped_url;
    if (props._file_upload_back_preview) return props._file_upload_back_preview;

    return '';
  }

  // --- Cart data transformation ---

  function transformCartItem(item) {
    const previewUrl = getPreviewImageUrl(item);
    const backPreviewUrl = getBackPreviewImageUrl(item);

    const transformed = {
      ProductID: item.product_id,
      VariantID: item.variant_id,
      SKU: item.sku || '',
      ProductName: item.product_title,
      VariantName: item.variant_title || '',
      Quantity: item.quantity,
      ItemPrice: (item.price / 100).toFixed(2),
      RowTotal: (item.line_price / 100).toFixed(2),
      ProductURL: config.shopUrl + item.url,
      ImageURL: previewUrl
    };

    if (backPreviewUrl) {
      transformed.BackImageURL = backPreviewUrl;
    }

    return transformed;
  }

  async function fetchFullCart() {
    try {
      const response = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error('Cart API returned ' + response.status);
      return await response.json();
    } catch (err) {
      log('Error fetching /cart.js:', err);
      return null;
    }
  }

  function buildEventPayload(cartData, addedItem) {
    const items = (cartData.items || []).map(transformCartItem);
    const cartTotal = (cartData.total_price / 100).toFixed(2);

    const addedItemTransformed = addedItem && addedItem.variant_id
      ? items.find(i => i.VariantID === addedItem.variant_id)
      : null;

    return {
      '$event_id': 'cart_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
      '$value': parseFloat(cartTotal),
      CartTotal: cartTotal,
      CartCurrency: config.currency,
      ItemCount: cartData.item_count || items.length,
      CheckoutURL: config.shopUrl + '/checkout',
      Items: items,

      AddedItemProductName: addedItem?.product_title || '',
      AddedItemProductID: addedItem?.product_id || '',
      AddedItemVariantID: addedItem?.variant_id || '',
      AddedItemSKU: addedItem?.sku || '',
      AddedItemPrice: addedItem ? ((addedItem.price || 0) / 100).toFixed(2) : '',
      AddedItemQuantity: addedItem?.quantity || 1,
      AddedItemImageURL: addedItemTransformed ? addedItemTransformed.ImageURL : '',
      AddedItemURL: addedItem?.url ? config.shopUrl + addedItem.url : ''
    };
  }

  // --- Push event to Klaviyo ---

  async function pushKlaviyoEvent(addedItem) {
    try {
      const cartData = await fetchFullCart();

      if (!cartData) {
        log('Could not fetch cart data, skipping');
        return;
      }

      const payload = buildEventPayload(cartData, addedItem);
      log('Event payload:', payload);

      if (window._learnq) {
        window._learnq.push(['track', EVENT_NAME, payload]);
        log('Event pushed to Klaviyo successfully');
      } else {
        log('_learnq not available, event not pushed');
      }
    } catch (err) {
      log('Error in Klaviyo cart tracking:', err);
    }
  }

  // --- AJAX cart add handler ---

  async function handleCartUpdated(event) {
    // Guard: only fire for actual add-to-cart events
    if (!event.detail || !event.detail.product_id) {
      log('Skipping - not an add-to-cart event');
      return;
    }

    // Debounce: prevent duplicate events from rapid additions
    const now = Date.now();
    if (now - lastEventTime < DEBOUNCE_MS) {
      log('Skipping - debounce active');
      return;
    }
    lastEventTime = now;

    log('Cart add detected (AJAX), preparing Klaviyo event...');
    await pushKlaviyoEvent(event.detail);
  }

  // --- SessionStorage flag detection ---
  // The toast system sets sessionStorage flags on every cart add path:
  //   1. Post-reload (PitchPrint natural POST + page reload)
  //   2. No-reload (Be Yours theme intercepts POST, opens cart drawer)
  // We check once at init (for reloads) then poll for new flags (for no-reload).

  const FLAG_KEY = 'pitchprint_show_success_toast';
  const TIMESTAMP_KEY = 'pitchprint_success_timestamp';
  const KLAVIYO_SENT_KEY = 'klaviyo_cart_event_sent';
  const FLAG_MAX_AGE_MS = 10000;

  async function checkCartAddFlags() {
    try {
      const shouldShow = sessionStorage.getItem(FLAG_KEY);
      const timestamp = sessionStorage.getItem(TIMESTAMP_KEY);
      const alreadySent = sessionStorage.getItem(KLAVIYO_SENT_KEY);

      if (!shouldShow || !timestamp) return false;

      const timeDiff = Date.now() - parseInt(timestamp, 10);
      if (timeDiff > FLAG_MAX_AGE_MS) return false;

      if (alreadySent === timestamp) return false;

      log('Cart add detected via sessionStorage flags (' + timeDiff + 'ms ago), pushing Klaviyo event...');

      // Mark as sent to prevent duplicates
      sessionStorage.setItem(KLAVIYO_SENT_KEY, timestamp);
      lastEventTime = Date.now();

      await pushKlaviyoEvent(null);
      return true;
    } catch (err) {
      log('Error checking sessionStorage flags:', err);
      return false;
    }
  }

  // Poll for new flags (catches no-reload cart adds like file upload via Be Yours theme)
  let flagWatcherInterval = null;
  const WATCHER_POLL_MS = 1000;
  const WATCHER_MAX_LIFETIME_MS = 300000; // 5 minutes

  function startFlagWatcher() {
    const startTime = Date.now();

    flagWatcherInterval = setInterval(async () => {
      // Stop after max lifetime to avoid memory leaks
      if (Date.now() - startTime > WATCHER_MAX_LIFETIME_MS) {
        log('Flag watcher expired, stopping');
        clearInterval(flagWatcherInterval);
        return;
      }

      await checkCartAddFlags();
    }, WATCHER_POLL_MS);
  }

  // --- Initialization ---

  async function init() {
    log('Initializing...');

    const klaviyoReady = await waitForKlaviyo();
    if (klaviyoReady && config.customer.loggedIn) {
      identifyCustomer();
    }

    // Check for cart add flags (post-reload case)
    await checkCartAddFlags();

    // Poll for new flags (no-reload case: Be Yours theme intercepts form POST)
    startFlagWatcher();

    // Listen for AJAX cart additions
    document.addEventListener('cart:updated', handleCartUpdated);
    log('Initialized - listening for cart:updated + polling sessionStorage flags');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
